import type { Translation } from '../types';

export const de: Translation = {
  meta: { name: 'Deutsch', english: 'German', tag: 'de', dir: 'ltr' },

  common: {
    menu: "Menü",
    cancel: 'Abbrechen',
    done: 'Fertig',
    join: 'Beitreten',
    leave: 'Verlassen',
    refresh: 'Aktualisieren',
    save: 'Speichern',
    you: 'du',
    host: 'Host',
    ai: 'KI',
    ready: 'Bereit',
    notReady: 'Nicht bereit',
  },

  conn: {
    live: 'Verbunden',
    connecting: 'Verbinde',
    offline: 'Offline',
    connectingFull: 'Verbinde…',
    reconnectBanner: 'Verbindung verloren — Verbindungsaufbau läuft. Dein Platz bleibt 3 Minuten reserviert.',
  },

  nickname: {
    pickTitle: 'Wähle einen Namen',
    changeTitle: 'Namen ändern',
    subtitle: 'So sehen dich die anderen Spieler.',
    placeholder: 'z. B. Wild Vier',
    label: 'Spitzname',
    letsPlay: 'Los geht’s',
  },

  lobby: {
    heroTitle: 'UNO mit Freunden spielen',
    heroSubtitle: 'Keine Anmeldung. Link teilen und austeilen.',
    createRoom: 'Raum erstellen',
    roomCode: 'Raumcode',
    publicRooms: 'Öffentliche Räume',
    empty: 'Noch keine öffentlichen Räume — erstelle den ersten.',
    cardMeta: 'Host {host} · {seconds}s pro Zug',
    stacking: 'Stapeln',
    badgeOpen: 'Offen',
    badgeFull: 'Voll',
    badgeLive: 'Im Spiel',
    lockedTitle: 'Raum {id} ist gesperrt',
    password: 'Passwort',
    passwordLabel: 'Raumpasswort',
  },

  create: {
    title: 'Neuer Raum',
    roomName: 'Raumname',
    roomNamePlaceholder: 'UNO am Freitagabend',
    maxPlayers: 'Max. Spieler',
    playersOption: { other: '{count} Spieler' },
    passwordOptional: 'Passwort (optional)',
    passwordPlaceholder: 'Leer lassen für offenen Raum',
    listPublic: 'In der öffentlichen Raumliste anzeigen',
    houseRules: 'Hausregeln',
    submit: 'Erstellen',
  },

  rules: {
    turnTimer: 'Zugzeit',
    turnTimerHint: 'Läuft sie ab, spielt ein Bot eine zufällige gültige Karte für dich.',
    startingHand: 'Startkarten',
    unoPenalty: 'UNO-Strafe',
    stackDrawTwo: '+2 stapeln',
    stackDrawTwoHint: 'Auf eine +2 mit einer weiteren +2 antworten, statt zu ziehen',
    stackDrawFour: '+4 stapeln',
    stackDrawFourHint: 'Auf eine +4 mit einer weiteren +4 antworten',
    challengeWildFour: '+4 anfechten',
    challengeWildFourHint: 'Der nächste Spieler darf eine unzulässige +4 anfechten',
    strictWildFour: 'Strenge +4',
    strictWildFourHint: 'Nur spielbar, wenn du keine Karte der aktiven Farbe hast',
    playDrawn: 'Gezogene Karte spielen',
    playDrawnHint: 'Eine gezogene Karte darf sofort gespielt werden',
    drawUntilPlayable: 'Ziehen bis spielbar',
    drawUntilPlayableHint: 'So lange ziehen, bis eine gültige Karte kommt',
    hostOnly: 'nur der Host kann das ändern',
  },

  room: {
    copyLink: 'Einladungslink kopieren',
    copied: '✓ Kopiert',
    roomLabel: 'Raum',
    locked: 'gesperrt',
    public: 'öffentlich',
    unlisted: 'nicht gelistet',
    readyStatus: '{ready} von {total} bereit',
    botsJoining: { one: '{count} Bot kommt dazu', other: '{count} Bots kommen dazu' },
    startsWhenReady: 'das Spiel startet, sobald alle bereit sind.',
    needMorePlayers: 'Zum Start werden {min} Spieler gebraucht — lade jemanden ein oder füge einen Bot hinzu.',
    playersTitle: 'Spieler',
    botsTitle: 'KI-Bots',
    botsHostOnly: 'nur der Host kann das ändern',
    noBots: 'Keine Bots',
    botsWillJoin: { one: '{count} Bot spielt mit', other: '{count} Bots spielen mit' },
    botsHint: 'Sie setzen sich beim Spielstart an den Tisch. Aktuell passen bis zu {max}.',
    botsRoomFull: 'Der Raum ist voll — kein Platz für Bots.',
    soloCta: 'Allein unterwegs? Bot hinzufügen und starten',
    addBot: 'Bot hinzufügen',
    removeBot: 'Bot entfernen',
    removePlayer: '{name} entfernen',
    botPlaceholder: 'Bot {n}',
    defaultRoomName: '{name}s Raum',
    imReady: 'Ich bin bereit',
    cancelReady: 'Bereit zurücknehmen',
    offlineTag: 'offline',
  },

  game: {
    yourTurn: 'Du bist dran',
    yourTurnStack: 'Du bist dran — stapeln oder +{count} ziehen',
    othersTurn: '{name} ist dran',
    waiting: 'Warten…',
    gameOver: 'Spiel vorbei',
    challengeWindow: 'Wild Draw Four — Anfechtung möglich',
    drawPileLeft: 'noch {count}',
    discard: 'Ablage',
    draw: 'Ziehen',
    takeCards: '+{count} nehmen',
    pass: 'Passen',
    uno: 'UNO!',
    judge: 'RÜGEN',
    cardCount: { one: '{count} Karte', other: '{count} Karten' },
    clockwise: 'Im Uhrzeigersinn',
    counterClockwise: 'Gegen den Uhrzeigersinn',
    activeColour: 'Aktive Farbe',
    noCards: 'Keine Karten mehr — stark.',
    oneCard: 'nur noch eine!',
    unoTag: 'UNO',
    forfeitedTag: 'aufgegeben',
    offlineFor: 'offline {seconds}s',
    placeTag: 'Platz {place}',
    pickColour: 'Farbe wählen',
    challengeTitle: '{name} hat eine Wild Draw Four gespielt. Anfechten?',
    challengeExplain: 'War es geblufft, zieht die Person {bluff}. Sonst ziehst du {fail}.',
    challengeBtn: 'Anfechten',
    acceptBtn: '+{count} nehmen',
    youWin: '🎉 Du gewinnst!',
    standingsTitle: 'Spiel vorbei',
    points: '{points} Pkt.',
    forfeited: 'aufgegeben',
    backToLobby: 'Zurück zur Lobby',
    playAgain: 'Nochmal spielen',
    waitingOthers: 'Warte auf die anderen…',
    faceDownCard: 'Verdeckte Karte',
    sortByColour: "Nach Farbe sortieren",
    sortAsDealt: "Reihenfolge beibehalten",
    drawACard: 'Eine Karte ziehen',
    drawNCards: '{count} Karten ziehen',
  },

  events: {
    someone: 'Jemand',
    unoCalledYou: 'Du hast UNO gerufen!',
    unoCalledOther: '{name} hat UNO gerufen!',
    judged: '{accuser} hat {target} erwischt — +{count} Karten',
    judgedYou: '{accuser} hat dich erwischt — +{count} Karten',
    challengeBluff: 'Bluff aufgeflogen — {name} zieht {count}',
    challengeFailed: 'Anfechtung gescheitert — {name} zieht {count}',
    forfeited: '{name} hat aufgegeben.',
    afkYou: 'Deine Zeit ist abgelaufen — automatisch gespielt.',
    afkOther: '{name} hat die Zeit überschritten — automatisch gespielt.',
    hostChanged: '{name} ist jetzt Host.',
    reshuffled: 'Nachziehstapel neu gemischt.',
    youWin: 'Du gewinnst!',
    winner: '{name} gewinnt!',
    gameOver: 'Spiel vorbei.',
  },

  errors: {
    bad_request: 'Etwas ist schiefgelaufen.',
    no_session: 'Sitzung abgelaufen — neue Verbindung wird aufgebaut.',
    room_not_found: 'Diesen Raum gibt es nicht.',
    room_full: 'Dieser Raum ist voll.',
    wrong_password: 'Falsches Passwort.',
    already_in_room: 'Verlasse zuerst deinen aktuellen Raum.',
    not_in_room: 'Du bist in keinem Raum.',
    not_host: 'Das kann nur der Host.',
    game_in_progress: 'Dieses Spiel läuft bereits.',
    not_enough_players: 'Nicht genug Spieler.',
    stale_action: 'Jemand war schneller — dein Zug wurde verworfen.',
    illegal_action: 'Dieser Zug ist nicht erlaubt.',
    rate_limited: 'Nicht so schnell.',
    nickname_invalid: 'Dieser Spitzname geht nicht.',
  },

  theme: {
    title: 'Kartendesign',
    description: 'Fehlende Bilder fallen auf das eingebaute CSS-Deck zurück.',
    builtIn: 'Eingebaut — keine Dateien nötig',
    pngHint: 'Lege 300×450-PNGs in {path} und trage sie im Theme-Index ein.',
  },

  language: {
    title: 'Sprache',
    description: 'Gilt sofort und wird auf diesem Gerät gespeichert.',
  },

  cards: {
    red: "Rot",
    yellow: "Gelb",
    green: "Grün",
    blue: "Blau",
    wild: "Wild",
    skip: "Aussetzen",
    reverse: "Retour",
    draw2: "+2",
    wildCard: "Wild",
    wild4: "+4",
  },

  log: {
    title: "Spielverlauf",
    empty: "Noch nichts.",
    close: "Verlauf ausblenden",
    timestamps: "Zeitstempel",
    chatPlaceholder: "Sag etwas …",
    send: "Senden",
    played: "{name} legt {card}",
    drew: "{name} zieht {count}",
    skipped: "{name} muss aussetzen",
    reversed: "Richtung gewechselt",
    joined: "{name} ist beigetreten",
    left: "{name} hat verlassen",
    started: "Spiel gestartet",
  },

  profile: {
    avatar: "Avatar",
    avatarHint: "Wähle einen Avatar oder behalte deinen Anfangsbuchstaben.",
  },

  leave: {
    title: "Spiel verlassen?",
    body: "Die Runde läuft noch. Wenn du gehst, gibst du auf und deine Karten wandern zurück ins Deck.",
    stay: "Weiterspielen",
    confirm: "Trotzdem gehen",
    unload: "Du bist mitten in einem Spiel.",
  },
};
